import { useInfiniteQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type FeedItem =
  | {
      type: 'announcement'
      id: string
      title: string
      body: string
      priority: 'normal' | 'important' | 'urgent'
      category: string | null
      image_url: string | null
      published_at: string
      author_entity_name: string
      target_entity_id: string
      target_entity_level: string | null
    }
  | {
      type: 'news'
      id: string
      title: string
      summary: string | null
      link: string
      image_url: string | null
      published_at: string
      source_id: string
      source_name: string
      source_short_name: string | null
      source_brand_color: string | null
    }

// Higher number = shown first (top of feed)
// Superadmin announcements (no entity / null level) get highest rank
const ENTITY_LEVEL_RANK: Record<string, number> = {
  region: 4,
  province: 3,
  city_municipality: 2,
  barangay: 1,
}

function getEntityLevelRank(level: string | null): number {
  if (!level) return 5 // superadmin / system-wide → highest
  return ENTITY_LEVEL_RANK[level] ?? 0
}

const PAGE_SIZE = 15

export function useNewsFeed() {
  return useInfiniteQuery<FeedItem[]>({
    queryKey: ['news-feed'],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = (pageParam as number) * PAGE_SIZE

      // Fetch announcements with target entity level for sort hierarchy
      const { data: announcements, error: aErr } = await supabase
        .from('announcements')
        .select(`
          id, title, body, priority, category, image_storage_path,
          published_at, target_entity_id,
          author_entity:entities!announcements_author_entity_id_fkey(name),
          target_entity:entities!announcements_target_entity_id_fkey(level)
        `)
        .eq('status', 'published')
        .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
        .order('published_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1)

      if (aErr) throw aErr

      // Fetch news (everyone sees same news)
      const { data: news, error: nErr } = await supabase
        .from('news_items')
        .select(`
          id, title, summary, link, image_url, published_at, source_id,
          source:news_sources(name, short_name, brand_color)
        `)
        .order('published_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1)

      if (nErr) throw nErr

      // Process announcements — each independently so one failure doesn't kill them all
      const announcementItems: FeedItem[] = []

      if (announcements && announcements.length > 0) {
        for (let i = 0; i < announcements.length; i++) {
          const a = announcements[i]
          try {
            let image_url: string | null = null
            if (a.image_storage_path) {
              try {
                const { data } = await supabase.storage
                  .from('announcement-images')
                  .createSignedUrl(a.image_storage_path, 3600)
                image_url = data?.signedUrl ?? null
              } catch {
                image_url = null
              }
            }

            announcementItems.push({
              type: 'announcement' as const,
              id: a.id,
              title: a.title,
              body: a.body ?? '',
              priority:
                (a.priority as 'normal' | 'important' | 'urgent') ?? 'normal',
              category: a.category ?? null,
              image_url,
              published_at: a.published_at,
              author_entity_name:
                (a.author_entity as unknown as { name: string } | null)
                  ?.name ?? 'Local Authority',
              target_entity_id: a.target_entity_id,
              target_entity_level:
                (a.target_entity as unknown as { level: string } | null)
                  ?.level ?? null,
            })
          } catch {
            // Skip this announcement, continue with others
          }
        }
      }

      const newsItems: FeedItem[] = (news ?? []).map((n: any) => ({
        type: 'news' as const,
        id: n.id,
        title: n.title,
        summary: n.summary,
        link: n.link,
        image_url: n.image_url,
        published_at: n.published_at,
        source_id: n.source_id,
        source_name: n.source?.name ?? 'News',
        source_short_name: n.source?.short_name ?? null,
        source_brand_color: n.source?.brand_color ?? null,
      }))

      // Sort order:
      // 1. ALL announcements above ALL news
      // 2. Within announcements: by entity level hierarchy (superadmin > region > province > city > barangay)
      // 3. Within same level: urgent > important > normal
      // 4. Within same priority: newest first
      // 5. News items: newest first
      const merged = [...announcementItems, ...newsItems]
      merged.sort((a, b) => {
        // Announcements always above news
        const aIsAnn = a.type === 'announcement' ? 1 : 0
        const bIsAnn = b.type === 'announcement' ? 1 : 0
        if (aIsAnn !== bIsAnn) return bIsAnn - aIsAnn

        // Both are announcements — sort by entity level hierarchy
        if (a.type === 'announcement' && b.type === 'announcement') {
          const aLevel = getEntityLevelRank(a.target_entity_level)
          const bLevel = getEntityLevelRank(b.target_entity_level)
          if (aLevel !== bLevel) return bLevel - aLevel

          // Same level — sort by priority
          const priorityRank = { urgent: 2, important: 1, normal: 0 }
          const aPri = priorityRank[a.priority] ?? 0
          const bPri = priorityRank[b.priority] ?? 0
          if (aPri !== bPri) return bPri - aPri
        }

        // Same type and priority — newest first
        return (
          new Date(b.published_at).getTime() -
          new Date(a.published_at).getTime()
        )
      })

      return merged
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined
      return allPages.length
    },
    staleTime: 60 * 1000,
  })
}
